// rasterCanvas — メインスレッドで「2D コンテキスト付きの作業用 canvas」を得る共通口。
//
// 背景 (Sentry WEB-5, 2026-08-13):
//   snapshot デコード / imagepaste デコード / 過去キャンバスのサムネ合成は、いずれも
//   **`OffscreenCanvas` を無条件に要求**していた。ところが **OffscreenCanvas の 2D は
//   Safari 16.4 で初めて載った**機能で、**iOS 16.3 以前には存在しない**。
//   本番で iOS 16.2 の iPad (LINE アプリ内ブラウザ = WKWebView) が
//   `OffscreenCanvas unavailable` で落ちていた。LINE 固有ではなく、Safari で開いても
//   同じ — WKWebView も Safari も同一の WebKit 版に従うため。
//
//   これらは**すべてメインスレッドで動く** (`SessionProvider` / `CanvasDrawingEngine` /
//   `PastCanvasListModal` から呼ばれる) ので、`document` が使える。つまり
//   **OffscreenCanvas を要求する理由がそもそも無く**、通常の `<canvas>` で代替できる。
//   worker 内の描画 (`drawingWorker.ts`) は本当に OffscreenCanvas が要るので対象外
//   (そちらは `DrawingCanvas` が非対応環境で inline 経路に落とす設計で元から守られている)。
//
// 方針: **OffscreenCanvas があればそれを使い、無ければ `<canvas>` に落ちる**。両方
// 無いとき (= node テストで stub を渡し忘れた等) だけ null を返し、呼び出し側が
// 「設定ミス」として扱えるようにする。

/** 2D 描画に使える canvas と、その context のペア。 */
export interface Raster2d {
  readonly canvas: OffscreenCanvas | HTMLCanvasElement;
  readonly ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
}

/**
 * `width` × `height` の作業用 canvas を作る。OffscreenCanvas → `<canvas>` の順に
 * 試し、どちらも使えなければ `null`。
 */
export const createRaster2d = (width: number, height: number): Raster2d | null => {
  if (typeof OffscreenCanvas !== 'undefined') {
    const oc = new OffscreenCanvas(width, height);
    const ctx = oc.getContext('2d');
    if (ctx !== null) return { canvas: oc, ctx };
    oc.width = 0;
    oc.height = 0;
  }
  if (typeof document !== 'undefined') {
    const el = document.createElement('canvas');
    el.width = width;
    el.height = height;
    const ctx = el.getContext('2d');
    if (ctx !== null) return { canvas: el, ctx };
    el.width = 0;
    el.height = 0;
  }
  return null;
};

/**
 * 作業用 canvas の backing store を即解放する。GC を待たずに 0×0 へ縮めるのが
 * WebKit では確実 (canvas メモリ予算は GC 待ちだと iPad で枯渇し、以後の
 * getContext が null / getImageData が透明を返す)。
 */
export const releaseRaster2d = (raster: Raster2d): void => {
  try {
    raster.canvas.width = 0;
    raster.canvas.height = 0;
  } catch {
    // best-effort
  }
};

/**
 * `ImageBitmap` を生の RGBA に展開する。snapshot / imagepaste 双方のデコード実体。
 *
 * 2D コンテキストが一切得られない環境では throw する (= 呼び出し側が decodeFn を
 * 注入し忘れた設定ミス)。**ブラウザでは throw しない** — それが WEB-5 の再発防止。
 */
export const decodeBitmapToRgba = (
  bitmap: ImageBitmap,
): { rgba: Uint8ClampedArray; width: number; height: number } => {
  const raster = createRaster2d(bitmap.width, bitmap.height);
  if (raster === null) {
    throw new Error('no 2D canvas available (neither OffscreenCanvas nor document)');
  }
  try {
    raster.ctx.drawImage(bitmap, 0, 0);
    const img = raster.ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    // `ImageData.data` は Uint8ClampedArray だが、canvas の backing memory が GC される
    // 可能性があるので呼び出し側が所有するコピーを返す (旧実装踏襲)。
    return { rgba: new Uint8ClampedArray(img.data), width: bitmap.width, height: bitmap.height };
  } finally {
    // 成功・失敗どちらでも一時 canvas を即解放する。
    releaseRaster2d(raster);
  }
};

/**
 * `Raster2d` の中身を PNG `Blob` にする。OffscreenCanvas なら `convertToBlob`、
 * `<canvas>` なら `toBlob` と API が違うのでここで吸収する。
 */
export const raster2dToBlob = async (
  raster: Raster2d,
  type = 'image/png',
): Promise<Blob | null> => {
  const { canvas } = raster;
  if (typeof OffscreenCanvas !== 'undefined' && canvas instanceof OffscreenCanvas) {
    return await canvas.convertToBlob({ type });
  }
  const el = canvas as HTMLCanvasElement;
  return await new Promise<Blob | null>((resolve) => {
    el.toBlob((blob) => resolve(blob), type);
  });
};
